import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUsers } from 'react-icons/fi';
import { useAbonnements, useAbonnementActions } from '../hooks/useAbonnements';
import { useSearch } from '../contexts/SearchContext';
import '../styles/Abonnements.css';

const Abonnements = () => {
  const navigate = useNavigate();
  const { globalSearch } = useSearch();
  const { data, loading, error, refetch } = useAbonnements();
  const { togglePlanStatus, deletePlan } = useAbonnementActions();
  const [filter, setFilter] = useState('Mois');

  const handleCreateAbonnement = () => {
    navigate('/abonnements/create');
  };

  const handleModifyPlan = (planId) => {
    console.log('🔍 Modification plan avec ID:', planId);
    console.log('🔍 Type d\'ID:', typeof planId);
    navigate(`/abonnements/edit/${planId}`);
  };

  if (loading) {
    return <div className="loading">Chargement des abonnements...</div>;
  }

  if (error) {
    return <div className="error-message">Erreur: {error}</div>;
  }

  const { plans = [], stats = {} } = data || { plans: [], stats: {} };

  // Utiliser les vraies données de l'API
  const statsData = {
    totalSubscribers: stats.totalSubscribers || 0,
    renewalRate: stats.renewalRate || 0
  };

  // Configuration des couleurs et noms d'affichage par type de plan
  const getSubscriptionPlans = () => {
    const planConfig = {
      'Standard Test': {
        color: '#3B82F6',
        displayName: 'Basic'
      },
      'Standard': {
        color: '#3B82F6', 
        displayName: 'Basic'
      },
      'Premium': {
        color: '#10B981',
        displayName: 'Standard'
      },
      'Enterprise': {
        color: '#F59E0B',
        displayName: 'Premium'
      }
    };

    return plans.map(plan => {
      const config = planConfig[plan.name] || planConfig['Standard'];
      
      // Calculer les services basés sur les features API
      const services = [];
      if (plan.features?.one_service_type) services.push('✓ 1 type de service actif (transport ou livraison ou location)');
      if (plan.features?.up_to_3_services) services.push('✓ Jusqu\'à 3 services actifs en simultané');
      if (plan.features?.unlimited_services) services.push('✓ Accès illimité à tous les types de services');
      if (plan.features?.map_visibility) services.push('✓ Profil visible sur la carte et dans les recherches');
      if (plan.features?.priority_display) services.push('✓ Affichage prioritaire dans les résultats de recherche');
      if (plan.features?.premium_visibility) services.push('✓ Visibilité premium dans les résultats');
      if (plan.features?.email_support) services.push('✓ Support client par e-mail');
      if (plan.features?.complete_dashboard) services.push('✓ Accès complet au tableau de bord');
      if (plan.features?.verified_badge) services.push('✓ Badge vérifié + service client prioritaire');

      // Calculer les limites basées sur les features API
      const limits = [];
      if (plan.features?.monthly_limit_30) limits.push('✓ 30 trajets ou livraisons maximum par mois');
      if (plan.features?.monthly_limit_150) limits.push('✓ 150 trajets ou livraisons maximum par trimestre');
      if (plan.features?.monthly_limit_500) limits.push('✓ 500 trajets ou livraisons maximum par an');
      if (plan.features?.one_vehicle_only) limits.push('✓ 1 seul véhicule déclaré');
      if (plan.features?.up_to_3_vehicles) limits.push('✓ Jusqu\'à 3 véhicules déclarés');
      if (plan.features?.up_to_10_vehicles) limits.push('✓ Jusqu\'à 10 véhicules déclarés');
      if (plan.features?.unlimited_reservations) limits.push('✓ Réservations simultanées illimitées');
      if (plan.features?.no_advanced_stats) limits.push('✓ Pas d\'accès aux statistiques avancées');
      if (plan.features?.limited_stats) limits.push('✓ Accès limité aux statistiques');

      // Calculer la durée basée sur la période de facturation
      const getDuration = (billingPeriod) => {
        switch(billingPeriod) {
          case 'monthly': return 'Mensuelle';
          case 'quarterly': return 'Trimestrielle'; 
          case 'yearly': return 'Annuelle';
          default: return 'Mensuelle';
        }
      };

      return {
        ...plan,
        color: config.color,
        revenue: new Intl.NumberFormat('fr-FR').format(plan.total_revenue || 0) + ' FCFA',
        revenueLabel: 'Revenu généré',
        monthlyPrice: new Intl.NumberFormat('fr-FR').format(plan.price || 0) + ' FCFA',
        subscribers: plan.subscribers_count || 0,
        duration: getDuration(plan.billing_period),
        displayName: config.displayName,
        services: services.length > 0 ? services : ['✓ Aucun service configuré'],
        limits: limits.length > 0 ? limits : ['✓ Aucune limite configurée'],
        // Format d'affichage pour les détails
        realName: plan.name,
        realPrice: new Intl.NumberFormat('fr-FR').format(plan.price || 0) + ' FCFA',
        realBilling: plan.billing_period === 'monthly' ? 'Mensuel' : plan.billing_period === 'yearly' ? 'Annuel' : plan.billing_period === 'quarterly' ? 'Trimestriel' : 'Mensuel',
        realSubscribers: plan.subscribers_count || 0,
        realRevenue: new Intl.NumberFormat('fr-FR').format(plan.total_revenue || 0) + ' FCFA'
      };
    });
  };

  const subscriptionPlans = getSubscriptionPlans();
  console.log('📋 Plans récupérés:', subscriptionPlans.map(p => ({id: p.id, name: p.name, displayName: p.displayName})));
  console.log('📋 Plans API bruts:', plans.map(p => ({id: p.id, name: p.name})));

  return (
    <div className="abonnements-container">
      <div className="abonnements-header">
        <h1>Abonnement</h1>
        <button className="btn-create" onClick={handleCreateAbonnement}>
          <span className="plus-icon">+</span>
          Créer un abonnement
        </button>
      </div>

      <div className="filter-section">
        <select 
          value={filter} 
          onChange={(e) => setFilter(e.target.value)}
          className="filter-select"
        >
          <option value="Mois">Mois</option>
          <option value="Trimestre">Trimestre</option>
          <option value="Année">Année</option>
        </select>
      </div>

      {/* Section statistiques haute */}
      <div className="top-stats-section">
        {/* Petites cartes statistiques */}
        <div className="small-stats">
          <div className="stat-card">
            <div className="stat-label">Nombre total d'abonnés</div>
            <div className="stat-value blue">{statsData.totalSubscribers}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Taux de renouvellement</div>
            <div className="stat-value blue">{statsData.renewalRate}%</div>
          </div>
        </div>

        {/* Grandes cartes colorées */}
        <div className="subscription-cards">
          {subscriptionPlans.slice(0, 3).map((plan) => (
            <div key={plan.id} className="subscription-card" style={{borderTopColor: plan.color}}>
              <div className="card-header">
                <h3 className="plan-title" style={{color: plan.color}}>
                  {plan.displayName}
                </h3>
                <div className="revenue-amount">{plan.revenue}</div>
                <div className="revenue-label">{plan.revenueLabel}</div>
              </div>
              <div className="card-footer">
                <span className="monthly-price">{plan.monthlyPrice} <span className="price-period">/mois</span></span>
                <span className="subscriber-count">
                  <FiUsers style={{marginRight: '4px'}} />
                  {plan.subscribers}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Plans détaillés en bas */}
      <div className="detailed-plans-section">
        {subscriptionPlans.slice(0, 3).filter(plan => {
          // Filtrage par recherche globale (du header)
          if (globalSearch) {
            const searchLower = globalSearch.toLowerCase();
            return (
              plan.name?.toLowerCase().includes(searchLower) ||
              plan.displayName?.toLowerCase().includes(searchLower) ||
              plan.description?.toLowerCase().includes(searchLower) ||
              plan.price?.toString().includes(searchLower)
            );
          }
          return true;
        }).map((plan) => {
          
          return (
          <div key={plan.id} className="detailed-plan-card">
            <div className="plan-header-detailed">
              <h3 className="plan-name-detailed">{plan.displayName}</h3>
              <div className="plan-price-detailed">{plan.realPrice}</div>
              <div style={{fontSize: '12px', color: '#64748b', marginTop: '4px'}}>
                Nom API: {plan.realName} | Facturation: {plan.realBilling}
              </div>
            </div>

            <div className="plan-content">
              <div className="plan-duration">
                <strong>Durée :</strong> {plan.duration}
              </div>

              <div className="plan-services">
                <strong className="section-title services-title">Services inclus :</strong>
                <ul className="services-list">
                  {plan.services.map((service, index) => (
                    <li key={index}>{service}</li>
                  ))}
                </ul>
              </div>

              <div className="plan-limits">
                <strong className="section-title limits-title">Limites :</strong>
                <ul className="limits-list">
                  {plan.limits.map((limit, index) => (
                    <li key={index}>{limit}</li>
                  ))}
                </ul>
              </div>

              <div className="plan-actions">
                <button 
                  className="btn-modifier"
                  onClick={() => handleModifyPlan(plan.id)}
                >
                  Modifier
                </button>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {subscriptionPlans.length === 0 && (
        <div className="empty-state">
          <p>Aucun plan d'abonnement configuré.</p>
          <button className="btn-create" onClick={handleCreateAbonnement}>
            Créer le premier plan
          </button>
        </div>
      )}
    </div>
  );
};

export default Abonnements;